// A FINISHED review member, persisted so a re-run of the same review reuses it (#6098 g4).
//
// A review fans out into a plan and its members — `group` (a cohort of paths),
// `concern` (one cross-cutting concern kind) and `handoff` (one reviewer handoff). Before
// this, nothing outlived the run: a review re-dispatched after a box death or a key hop
// redid every group it had already finished. Now the box writes each member as it settles
// (`fleet.req` `review_group_put`) and reads them back once at fan-out
// (`review_groups_get`); the mothership stores them in `review_group_results`
// (`@developerz/db`), keyed by what makes two answers interchangeable:
//
//   (repo, pr_number, head_sha, base_sha, review_digest, kind, member_key)
//
// - repo + PR come from the TASK on the mothership, never from the box;
// - `head_sha` / `base_sha` — the exact diff reviewed, 40-hex commits;
// - `review_digest` — sha256 of the resolved review config, the prompt layers' shas, the
//   per-reviewer settings and the ReviewResult schema version, computed by the box. A
//   changed prompt or config is a different digest, so a stale answer is never reused;
// - `member_key` — sha256 of (kind + sorted paths + concern kind / handoff id); `''` for
//   the one plan per review.
//
// FIRST WRITER WINS (`ON CONFLICT DO NOTHING`): a second put of the same key answers
// `existing` and changes nothing, so a retried or raced write is idempotent.
//
// `result` is the box's own answer, OPAQUE here (its shape is the runner's ReviewResult,
// versioned through `review_digest`). It is JSON by construction (`z.json()`), so what is
// stored is exactly what is read back. `v` versions THIS envelope: a stored row whose `v`
// this build does not know is a miss, never a guess.

import { z } from 'zod';

/** What a persisted member is. Stored values — a rename is a migration. */
export const ReviewGroupKind = {
  Plan: 'plan',
  Group: 'group',
  Concern: 'concern',
  Handoff: 'handoff',
} as const;
export const ReviewGroupKindSchema = z.enum(ReviewGroupKind);
export type ReviewGroupKind = z.infer<typeof ReviewGroupKindSchema>;

/** A full 40-hex git commit sha, lowercase. A short sha keys rows no full-sha read finds. */
export const ReviewCommitShaSchema = z.string().regex(/^[0-9a-f]{40}$/, 'a 40-hex commit sha');

/** A lowercase sha256 hex digest. */
export const ReviewDigestSchema = z.string().regex(/^[0-9a-f]{64}$/, 'a sha256 hex digest');

/** `''` for the plan, a sha256 hex digest for every other kind (enforced by the member schema). */
export const ReviewMemberKeySchema = z.union([z.literal(''), ReviewDigestSchema]);

/**
 * At most this many bytes of `result`, measured as its UTF-8 JSON encoding
 * ({@link reviewMemberResultBytes}). The failure it prevents: a `fleet.req` payload over
 * `NATS_MAX_PAYLOAD_BYTES` (1 MiB), which the transport refuses outright. A member over
 * it is not stored (`too_large`) and is simply re-run next time — never a failed review.
 */
export const REVIEW_MEMBER_RESULT_MAX_BYTES = 256 * 1024;

const utf8 = new TextEncoder();

/** The bytes `result` costs on the wire: UTF-8 length of `JSON.stringify(result)`. */
export function reviewMemberResultBytes(result: unknown): number {
  return utf8.encode(JSON.stringify(result) ?? '').byteLength;
}

/** The versioned body the `result` jsonb column stores. */
export const PersistedReviewBodySchema = z
  .object({
    v: z.literal(1),
    result: z.json(),
  })
  .strict();
export type PersistedReviewBody = z.infer<typeof PersistedReviewBodySchema>;

/**
 * One finished member, as the box puts it and gets it back (v1). `provider` / `model` /
 * `key_id` record WHO answered (a hop may have moved the key mid-review), informational
 * only — the cache key never includes them, because the answer is the same answer
 * whichever key produced it.
 */
export const PersistedReviewMemberSchema = z
  .object({
    v: z.literal(1),
    kind: ReviewGroupKindSchema,
    member_key: ReviewMemberKeySchema,
    result: z.json(),
    provider: z.string().min(1).max(64).nullable(),
    model: z.string().min(1).max(200).nullable(),
    key_id: z.string().min(1).max(64).nullable(),
  })
  .strict()
  .superRefine((m, ctx) => {
    if ((m.kind === ReviewGroupKind.Plan) !== (m.member_key === '')) {
      ctx.addIssue({
        code: 'custom',
        path: ['member_key'],
        message: `member_key must be '' for a plan and a sha256 digest for a ${m.kind}`,
      });
    }
    const bytes = reviewMemberResultBytes(m.result);
    if (bytes > REVIEW_MEMBER_RESULT_MAX_BYTES) {
      ctx.addIssue({
        code: 'custom',
        path: ['result'],
        message: `result is ${bytes} JSON-encoded bytes, over the ${REVIEW_MEMBER_RESULT_MAX_BYTES}-byte cap`,
      });
    }
  });
export type PersistedReviewMember = z.infer<typeof PersistedReviewMemberSchema>;
